<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import useLoading from '../../../../Composables/useLoading.ts'
import useModal from '../../../../Composables/useModal.ts'
import type { FormCreatorType } from '../../../../Types/form'
import type { ElectronicInvoiceStatusHistory } from '../../../../Types/invoice'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import FormCreator from '../UI/Form/FormCreator.vue'
import LazyContent from '../UI/Loading/LazyContent.vue'
import ModalDialog from '../UI/Modal/ModalDialog.vue'

type FormDefParameters = {
  status: string | null
}

const props = defineProps<{
  invoiceId: number
}>()

const emit = defineEmits<{
  close: []
  save: [{ history: ElectronicInvoiceStatusHistory }]
}>()

const { t } = useI18n()
const { loading, setLoading } = useLoading(true)
const { loading: sending } = useLoading(false)
const { modal, modalId, open, close } = useModal({ prefix: 'electronic-invoice-anomaly-' })

const form = ref<FormCreatorType | null>(null)

const endpoint = computed<string>(() => `electronic-invoices/${props.invoiceId}/status-histories`)

const selectedStatus = computed<string | null>(() => form.value?.datas?.status || null)

const defParams = computed<FormDefParameters>(() => ({ status: selectedStatus.value }))
async function submit() {
  sending.value = true
  try {
    await form.value?.methods()
  } finally {
    sending.value = false
  }
}

function submitted(history: ElectronicInvoiceStatusHistory) {
  emit('save', { history })
  close()
}

function onFormLoad(formCreator: FormCreatorType) {
  form.value = formCreator
}

async function refreshFormDef() {
  await nextTick()
  await form.value?.methods('get')
}

onMounted(() => {
  open()
})
</script>

<template>
  <ModalDialog
    ref="modal"
    :id="modalId"
    :title="t('invoice.electronic_invoice.title.anomaly')"
    class-body="px-3"
    :close-with-backdrop="false"
    @icon-close="emit('close')"
  >
    <template #body>
      <FormCreator
        :endpoint="endpoint"
        :params="defParams"
        src-error="formelectronicinvoicestatushistory"
        class-to-add="d-none"
        @form-return="submitted"
        @update-data="onFormLoad"
        @is-loading="setLoading"
      />

      <LazyContent :loading="loading" :class="{ 'p-2': loading }">
        <div v-if="form" class="row">
          <div class="col-12">
            <FieldCreator
              name="status"
              form-ref="formelectronicinvoicestatushistory"
              :form-data="form"
              domain="invoice.electronic_invoice"
              type-value="string"
              label-top
              @get-select-change="refreshFormDef"
            />
          </div>
          <div v-if="selectedStatus" class="col-12">
            <FieldCreator
              name="reason"
              form-ref="formelectronicinvoicestatushistory"
              :form-data="form"
              domain="invoice.electronic_invoice"
              type-value="string"
              label-top
            />
          </div>
          <div class="col-12">
            <FieldCreator
              name="comment"
              form-ref="formelectronicinvoicestatushistory"
              :form-data="form"
              domain="invoice.electronic_invoice"
              label-top
            />
          </div>
        </div>
      </LazyContent>
    </template>

    <template v-if="!loading" #footer>
      <div class="d-flex w-100 justify-content-end">
        <ButtonAll margin="no" type="default" label="button.cancel" @click="close" />
        <ButtonAll margin="no" type="action" label="button.confirm" class="ml-2" :loading="sending" @click="submit" />
      </div>
    </template>
  </ModalDialog>
</template>
